/**
 * Mobile keyboard handler for iOS/Android
 * 
 * When the on-screen keyboard appears, mobile browsers resize the visual viewport
 * which can push UI elements off-screen or cause layout issues. This utility:
 * 
 * 1. Detects keyboard open/close events via visualViewport API
 * 2. Updates CSS custom properties with keyboard height
 * 3. Prevents elastic scrolling and viewport shifting
 * 4. Maintains safe area insets properly
 * 
 * Usage:
 *   import { initKeyboardHandler } from './utils/keyboardHandler';
 *   initKeyboardHandler(); // Call once during app initialization
 */

let isKeyboardOpen = false;
let keyboardHeight = 0;
let visualViewport: VisualViewport | null = null;
let cleanupKeyboardHandler: (() => void) | undefined;
let layoutViewportHeight = 0;
let layoutViewportWidth = 0;
let resizeRafId = 0;

const KEYBOARD_THRESHOLD_PX = 120;

let settleTimeoutId = 0;
let appliedOffsetTop = 0;
let offsetWatchdogId = 0;

/**
 * In the native WKWebView shell the visual viewport already shrinks when the
 * keyboard opens (which lifts the flex composer above it), but the
 * scroll-to-focused-input counter-transform (--vv-offset-top) is meant for
 * Safari's PWA chrome and OVER-compensates here — it shoves the whole shell, and
 * the composer, up to the top of the thread. So keep the keyboard-height
 * detection but skip the counter-transform. The native bootstrap sets
 * window.__QUANTIS_NATIVE__ = true.
 */
function isNativeShell(): boolean {
  return typeof window !== 'undefined' && (window as { __QUANTIS_NATIVE__?: boolean }).__QUANTIS_NATIVE__ === true;
}

/**
 * Returns true when running as an installed standalone / fullscreen PWA.
 *
 * The shell-root counter-transform (translateY(-vv-offset-top)) is only
 * appropriate in this mode: iOS shifts visualViewport.offsetTop to scroll a
 * focused input into view, and the translate cancels that shift so the layout
 * stays locked. In a plain mobile-browser tab the browser handles
 * scroll-to-input itself without shifting the visual viewport — applying the
 * counter-transform there would shift the shell root ABOVE the browser chrome,
 * pushing the chat header out of view.
 */
function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: fullscreen)').matches === true ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

/**
 * The shell counters iOS's scroll-to-focused-input by translating up
 * `--vv-offset-top`. That offset MUST return to 0 once nothing is focused, or
 * the whole app stays shifted up after the keyboard closes. Backing out of a
 * chat unmounts the focused composer, and iOS frequently fires no
 * resize/scroll/focusout in that path — so the only reliable recovery is to
 * poll briefly while an offset is applied and clear it the moment focus has
 * left an editable. Self-arms on a non-zero offset, self-disarms at 0, so it
 * costs nothing outside the rare stranded window.
 */
function applyOffsetTop(): number {
  if (typeof document === 'undefined') return 0;
  // Native shell: the visual-viewport shrink already keeps the composer above
  // the keyboard; the scroll-to-focus counter-transform only over-shifts it to
  // the top in WKWebView, so never apply it here.
  if (isNativeShell()) {
    document.documentElement.style.setProperty('--vv-offset-top', '0px');
    appliedOffsetTop = 0;
    disarmOffsetWatchdog();
    return 0;
  }
  // No counter-transform anywhere any more. Measured live on the simulator
  // webclip (iOS 26): the layout viewport itself SHRINKS in place when the
  // keyboard opens (innerHeight == visualViewport.height), so the app
  // already fits the visible area exactly — the pan iOS reports via
  // offsetTop is just its scroll-to-focused-input residue, and translating
  // by it double-shifted the shell. What actually needs fighting is iOS
  // SCROLLING THE DOCUMENT (the 100lvh standalone sizing leaves slack below
  // the shrunken window) — pinDocumentScroll handles that.
  document.documentElement.style.setProperty('--vv-offset-top', '0px');
  appliedOffsetTop = 0;
  disarmOffsetWatchdog();
  pinDocumentScroll();
  return 0;
}

/**
 * The app is a fixed shell (html/body/root/shell-root all viewport-height,
 * overflow hidden) — the DOCUMENT must never scroll. iOS still scrolls it
 * for scroll-to-focused-input when the standalone 100lvh sizing leaves
 * scrollable slack under an open keyboard, shoving the whole app up
 * (measured: scrollY 413 with a 543px window). Snap it straight back;
 * in-app scroll containers handle their own reveal-on-focus.
 */
function pinDocumentScroll() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (isNativeShell() || !isStandalonePwa()) return;
  const scroller = document.scrollingElement;
  if (window.scrollY !== 0 || (scroller && scroller.scrollTop !== 0)) {
    window.scrollTo(0, 0);
    if (scroller) scroller.scrollTop = 0;
  }
}

/**
 * Force the shell's scroll-to-focus counter-transform (--vv-offset-top) back
 * to 0 immediately. iOS frequently dismisses the keyboard when a chat/composer
 * is closed WITHOUT firing a resize/blur, stranding the offset and leaving the
 * whole app shifted up. Views that unmount an editable (e.g. ChatWindow) call
 * this on teardown so the shell snaps back into place instead of waiting on an
 * event iOS may never send.
 */
export function resetKeyboardViewportOffset() {
  if (typeof document === 'undefined') return;
  const el = document.activeElement as HTMLElement | null;
  if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
    try { el.blur(); } catch { /* noop */ }
  }
  document.documentElement.style.setProperty('--vv-offset-top', '0px');
  appliedOffsetTop = 0;
  disarmOffsetWatchdog();
  recalculateViewportState();
}

function armOffsetWatchdog() {
  if (offsetWatchdogId || typeof window === 'undefined') return;
  offsetWatchdogId = window.setInterval(() => {
    if (appliedOffsetTop !== 0 && (!isEditableFocused() || !isKeyboardOpen)) {
      applyOffsetTop();
      recalculateViewportState();
    }
  }, 250);
}

function disarmOffsetWatchdog() {
  if (offsetWatchdogId) {
    clearInterval(offsetWatchdogId);
    offsetWatchdogId = 0;
  }
}

/**
 * iOS only shifts visualViewport.offsetTop when it scrolls to a focused input.
 * If nothing editable is focused, any non-zero offset is stale and must not
 * drive the shell's counter-transform — otherwise the shell stays translated
 * up after the keyboard closes (e.g. backing out of a chat unmounts the
 * focused composer without firing a visualViewport resize).
 */
function isEditableFocused(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Updates CSS variables that components can use to adjust for keyboard
 */
function updateKeyboardVars(emitEvent = true) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
  root.style.setProperty('--keyboard-open', isKeyboardOpen ? '1' : '0');
  if (visualViewport) {
    root.style.setProperty(
      '--visual-viewport-height',
      `${Math.round(visualViewport.height + visualViewport.offsetTop)}px`
    );
    // Counteract iOS visual viewport scroll-to-focused behavior.
    // When iOS focuses an input it shifts visualViewport.offsetTop > 0 which
    // visually pushes the entire app up. We expose this offset so CSS can
    // apply an equal-and-opposite translateY on the shell root. applyOffsetTop
    // forces 0 when nothing editable is focused and arms the watchdog so the
    // transform can never stay stranded after the keyboard closes.
    applyOffsetTop();
    root.style.setProperty(
      '--vv-height',
      `${Math.round(visualViewport.height)}px`
    );
  }
  root.setAttribute('data-keyboard-open', isKeyboardOpen ? '1' : '0');
  root.classList.toggle('keyboard-open', isKeyboardOpen);
  
  if (emitEvent) {
    // Dispatch custom event so components can react to keyboard changes
    window.dispatchEvent(new CustomEvent('keyboardchange', {
      detail: { isOpen: isKeyboardOpen, height: keyboardHeight }
    }));
  }
}

function getVisualViewportBottom() {
  if (!visualViewport) return window.innerHeight;
  return Math.round(visualViewport.height + visualViewport.offsetTop);
}

function recalculateViewportState() {
  if (typeof window === 'undefined' || !visualViewport) return;

  const visualViewportBottom = getVisualViewportBottom();
  const currentWidth = window.innerWidth;

  if (layoutViewportHeight === 0 || layoutViewportWidth === 0) {
    layoutViewportHeight = visualViewportBottom;
    layoutViewportWidth = currentWidth;
  }

  const widthChanged = Math.abs(currentWidth - layoutViewportWidth) > 64;
  if (widthChanged) {
    layoutViewportHeight = visualViewportBottom;
    layoutViewportWidth = currentWidth;
  }

  const baselineDiff = layoutViewportHeight - visualViewportBottom;
  if (!isKeyboardOpen && baselineDiff <= KEYBOARD_THRESHOLD_PX) {
    layoutViewportHeight = visualViewportBottom;
  }

  // Keyboard height from the VISUAL height alone, not height+offsetTop:
  // standalone webclips can answer a keyboard by PANNING the visual viewport
  // (offsetTop ≈ keyboard height, height shrunk) rather than shrinking in
  // place — height+offsetTop then stays ≈ full screen and the keyboard was
  // undetectable, so none of the keyboard layout ever engaged (diagnosed
  // live on the simulator PWA: offsetTop ~390 with kb reported 0).
  const nextKeyboardHeight = Math.max(0, layoutViewportHeight - Math.round(visualViewport.height));
  const nextKeyboardOpen = nextKeyboardHeight > KEYBOARD_THRESHOLD_PX;

  if (nextKeyboardOpen === isKeyboardOpen && nextKeyboardHeight === keyboardHeight) {
    // Keep non-keyboard variables fresh even when keyboard state is unchanged.
    updateKeyboardVars(false);
    return;
  }

  isKeyboardOpen = nextKeyboardOpen;
  keyboardHeight = nextKeyboardOpen ? nextKeyboardHeight : 0;
  updateKeyboardVars();
}

/**
 * Handle viewport resize to detect keyboard
 */
function handleViewportResize() {
  // Apply the visual-viewport counter-transform SYNCHRONOUSLY so there is no
  // single-frame flash where iOS has already shifted visualViewport.offsetTop
  // but the CSS hasn't compensated yet.  The full keyboard-open/height state
  // is still debounced via rAF to avoid thrashing layout during the animation.
  if (visualViewport && typeof document !== 'undefined') {
    const root = document.documentElement;
    applyOffsetTop();
    root.style.setProperty('--vv-height', `${Math.round(visualViewport.height)}px`);
    root.style.setProperty(
      '--visual-viewport-height',
      `${Math.round(visualViewport.height + visualViewport.offsetTop)}px`
    );
  }

  if (resizeRafId) cancelAnimationFrame(resizeRafId);
  resizeRafId = window.requestAnimationFrame(() => {
    resizeRafId = 0;
    recalculateViewportState();
  });
}

/**
 * Initialize keyboard handling
 */
export function initKeyboardHandler() {
  if (typeof window === 'undefined') return;
  if (cleanupKeyboardHandler) return cleanupKeyboardHandler;
  
  // Check if visualViewport API is available (iOS Safari 13+, Chrome Android 61+)
  if (!window.visualViewport) {
    console.warn('visualViewport API not available - keyboard handling disabled');
    return;
  }
  
  visualViewport = window.visualViewport;
  layoutViewportHeight = getVisualViewportBottom();
  layoutViewportWidth = window.innerWidth;

  // Set initial keyboard vars immediately (keyboard closed)
  updateKeyboardVars(false);
  
  // Listen to viewport resize events
  // Backing out of a chat blurs/unmounts the focused composer, but iOS often
  // dismisses the keyboard WITHOUT firing a visualViewport resize, leaving the
  // shell's counter-transform stranded. Re-read once the close animation has
  // settled so --vv-offset-top returns to 0.
  const onFocusOut = () => {
    handleViewportResize();
    if (settleTimeoutId) clearTimeout(settleTimeoutId);
    settleTimeoutId = window.setTimeout(() => {
      settleTimeoutId = 0;
      recalculateViewportState();
    }, 350);
  };

  // Re-assert offset when the PWA returns to the foreground — iOS can strand
  // a non-zero offset across a background/foreground cycle with no other event.
  const onVisibility = () => {
    if (document.visibilityState === 'visible') onFocusOut();
  };

  visualViewport.addEventListener('resize', handleViewportResize);
  visualViewport.addEventListener('scroll', handleViewportResize);
  window.addEventListener('resize', handleViewportResize);
  window.addEventListener('orientationchange', handleViewportResize);
  window.addEventListener('focusin', handleViewportResize);
  window.addEventListener('focusout', onFocusOut);
  document.addEventListener('visibilitychange', onVisibility);
  // iOS scrolls the DOCUMENT for scroll-to-focused-input without any
  // visualViewport event — catch it directly and snap back.
  window.addEventListener('scroll', pinDocumentScroll, { passive: true });
  
  // Initial setup
  recalculateViewportState();
  
  // Cleanup function (can be called on unmount if needed)
  cleanupKeyboardHandler = () => {
    if (visualViewport) {
      visualViewport.removeEventListener('resize', handleViewportResize);
      visualViewport.removeEventListener('scroll', handleViewportResize);
    }
    window.removeEventListener('resize', handleViewportResize);
    window.removeEventListener('orientationchange', handleViewportResize);
    window.removeEventListener('focusin', handleViewportResize);
    window.removeEventListener('focusout', onFocusOut);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('scroll', pinDocumentScroll);
    disarmOffsetWatchdog();
    if (resizeRafId) {
      cancelAnimationFrame(resizeRafId);
      resizeRafId = 0;
    }
    if (settleTimeoutId) {
      clearTimeout(settleTimeoutId);
      settleTimeoutId = 0;
    }

    visualViewport = null;
    isKeyboardOpen = false;
    keyboardHeight = 0;
    layoutViewportHeight = 0;
    layoutViewportWidth = 0;
    updateKeyboardVars();
    const root = document.documentElement;
    root.removeAttribute('data-keyboard-open');
    root.classList.remove('keyboard-open');
    root.style.removeProperty('--visual-viewport-height');
    root.style.removeProperty('--vv-height');
    root.style.removeProperty('--vv-offset-top');
    cleanupKeyboardHandler = undefined;
  };

  return cleanupKeyboardHandler;
}

/**
 * React hook for keyboard state
 */
export function useKeyboardState() {
  if (typeof window === 'undefined') {
    return { isOpen: false, height: 0 };
  }
  
  const [state, setState] = React.useState({ 
    isOpen: isKeyboardOpen, 
    height: keyboardHeight 
  });
  
  React.useEffect(() => {
    const handler = (e: CustomEvent) => {
      setState({ isOpen: e.detail.isOpen, height: e.detail.height });
    };
    
    window.addEventListener('keyboardchange', handler as EventListener);
    return () => window.removeEventListener('keyboardchange', handler as EventListener);
  }, []);
  
  return state;
}

// React import for hook (only imported when hook is used)
import * as React from 'react';